import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { DocumentsService } from '../../../services/documents.service';
import { NgForm } from '@angular/forms';
import { UrlService } from '../../../services/url.service';
import { ShareType } from '../../../types/documents/share-types';
import {AuthenticationService} from '../../../services/authentication.service';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators';

declare const toastr: any;

@Component({
    selector: 'edit-permissions-modal',
    templateUrl: './edit-permissions-modal.component.html',
    styleUrls: ['./edit-permissions-modal.scss']
})
export class EditPermissionsModalComponent implements OnInit, OnDestroy {

    public data: ItemObj = new ItemObj();
    public permissions: Array<any> = [];
    public permissionsList: Array<any> = [];
    public corporationNumbers: Array<any> = [];
    public buildings: Array<any> = [];
    public userGroups: Array<any> = [];
    public isPermissionsLoading: boolean = false;
    public isCorporationLoading: boolean = false;
    public isPublic: boolean = false;
    public name: string = '';
    public modalType: string = '';
    public isShared: boolean = false;
    
    private shareTypeValue: ShareType = null;

    public shareTypes = ShareType;
    public shareTypeOptions = Object.keys(ShareType).map(key => ({name: key, value: ShareType[key]}) );

    currentShareTypeOptions: Array<any> = this.shareTypeOptions;
    currentUserGroups: Array<any> = [];
    private ngUnsubscribe = new Subject();
    private parentPermissions = [];
    private initialPermissions = [];
    
    haveaParent = false;

    constructor(public ds: DocumentsService,
                public urlService: UrlService,
                public authenticationService: AuthenticationService) {
    }

    ngOnInit() {
        this.ds.corporationsListLoaded
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data) => {
                if (data) {
                    this.getCorporationNumberList();
                } else {
                    this.isCorporationLoading = true;
                }
            });
        this.loadPermissions();
        this.permissions = this.ds.currentItem.documentsPermissions.map(this.ds.parsePermission);
        this.userGroups = JSON.parse(JSON.stringify(this.ds.currentItem.userGroups));
        this.isPublic = this.ds.currentItem.isPublic;
        this.name = this.ds.currentItem.name;
        this.modalType = this.ds.currentItem.type == 'directory' ? 'Folder' : 'File';
        this.urlService.modalOpened.emit();
        this.initSareValues();
    }
    
    private loadPermissions(flag?): void {
        this.isPermissionsLoading = true;
        this.ds.getParentPermissions().subscribe(({ permissionsList,
            parentPermission,
            documentsPermissions,
            userGroups,
            isPublic
        }) => {
            this.isPermissionsLoading = false;
            this.permissionsList = permissionsList.map(this.ds.parsePermission);
            this.parentPermissions = parentPermission.map(this.ds.parsePermission);
            
            if (documentsPermissions !== null || userGroups !== null || isPublic !== null) {
                
                let shareTypeValue = null;
                if(isPublic) {
                    shareTypeValue = ShareType.Public;
                } else if (documentsPermissions.length) {
                    shareTypeValue = ShareType.Residents;
                } else if (userGroups.length) {
                    shareTypeValue = ShareType.Groups;
                }
                
                this.currentUserGroups = JSON.parse(JSON.stringify(userGroups.map(role => ({
                        id: role.bdId,
                        itemName: role.name
                    }))
                ));
                
                this.currentShareTypeOptions = this.shareTypeOptions.filter(item => item.value == shareTypeValue)

            } else { 
                this.currentUserGroups = JSON.parse(JSON.stringify(this.ds.userGroups));        
            }
            
            if (flag) { 
                this.isShared = flag;
            }
        });
    }

    initSareValues() {
        if(this.isPublic) {
            this.shareTypeValue = ShareType.Public;
        } else if (this.permissions.length) {
            this.shareTypeValue = ShareType.Residents;
        } else if (this.userGroups.length) {
            this.shareTypeValue = ShareType.Groups;
        } else {
            this.shareTypeValue = null;
        }
        this.isShared = this.shareTypeValue !== null;
    }

    set shareType(value: ShareType) {
        this.shareTypeValue = value;
        this.isShared = value !== null;
        
        this.isPublic = this.shareTypeValue === ShareType.Public;

        if(this.shareTypeValue !== ShareType.Residents) {
            this.permissions = []; // force reset permissions
        } else if (!this.permissions.length) {
            this.permissions = [...this.parentPermissions]; // set parent permissions as initial state
        }

        if(this.shareTypeValue !== ShareType.Groups) {
            this.userGroups = []; // force reset group sharing
        }
    }
  
    get shareType(): ShareType {
        return this.shareTypeValue;
    }

    onSubmit(form: NgForm) {
        let isInvalid = false;

        if(this.isShared && !this.shareType) {
            toastr.warning('Share option type should be selected');
            isInvalid = true;
        }

        if(this.shareType === ShareType.Groups && !(this.userGroups && this.userGroups.length)) {
            toastr.warning('Group should not be blank.');
            isInvalid = true;
        }

        if (this.shareType === ShareType.Residents && !(this.permissions && this.permissions.length)) {
            toastr.warning('Occupancy Types should not be blank.');
            isInvalid = true;
        }
        
        if (!this.ds.selectedItemData.corporationNumbers.length) { 
            toastr.warning('Corporation Numbers should not be blank.');
            isInvalid = true;
        }
        
        if (!this.ds.selectedItemData.buildings.length) { 
            toastr.warning('Buildings should not be blank.');
            isInvalid = true;
        }

        if(isInvalid) {
            return;
        }

        let permissions = [];
        let userGroups = [];
        
        this.isPublic = this.shareTypeValue === ShareType.Public;
        
        if (this.shareTypeValue === ShareType.Residents) {
            permissions = this.permissions.map((item) => ({permission: item.id}));
        }

        if (this.shareTypeValue === ShareType.Groups) {
            userGroups = this.userGroups.map(g => ({
                bdId: g.id,
                name: g.itemName
            }));
        }
        
        const corporationsAndBuildings = this.ds.getCorporationBuildingsData();
        
        const data = {
            corporationNumbers: corporationsAndBuildings.corporationNumbers,
            buildings: corporationsAndBuildings.buildings,
            documentsPermissions: permissions,
            userGroups,
            public: this.isPublic,
            isPublic: this.isPublic,
            name: this.name
        };
        
        this.ds.update(data, true);
    }

    onPermissionsChange(data): void {
        this.permissions = data;
    }
    
    onBuildingsChange() { 
        if (!this.ds.isPermissionChanged) { 
            this.ds.isPermissionChanged = true;
        }
    }

    getBuildingsForSelectedCorporations(event?, eventType?) {
        this.buildings = [];
        
        if (!this.haveaParent && this.data.corporationNumbers.length) {
            this.data.corporationNumbers.forEach(corp => {
                this.ds.buildingsForUpdate.forEach(building => {
                    if ((<any>building).corporationNumber == corp.id)
                        this.buildings.push({
                            ...building,
                            itemName: building.name,
                        });
                });
            });
        } else if (this.haveaParent && this.data.corporationNumbers.length > 0) {
            this.getParentBuildings();
        } else if (!this.haveaParent && this.corporationNumbers.length > 1) { 
            this.buildings = JSON.parse(JSON.stringify(this.ds.multiselectBuildings));
        }
        
        if(eventType == 'deselect')
            this.ds.selectedItemData.buildings = this.ds.selectedItemData.buildings.filter(b => b.corporationNumber != event.id);
        else if(eventType == 'deselectAll')
            this.ds.selectedItemData.buildings = [];
        
        if(this.buildings.length === 1 && this.ds.selectedItemData.corporationNumbers.length)
            this.ds.selectedItemData.buildings = JSON.parse(JSON.stringify(this.buildings));
        
        if (!this.ds.selectedItemData.corporationNumbers.length) { 
            this.ds.selectedItemData.buildings = [];
        }
    }
    
    getParentBuildings() { 
        let parentBuildings = this.ds.getParentBuildings();
        
        if (!parentBuildings.length) return;
        
        this.buildings = [];
        
        if (this.ds.selectedItemData) {
            this.ds.selectedItemData.buildings = [];
        }
        
        if (!this.data.corporationNumbers.length) return;
        
        parentBuildings.map(building => { 
            this.buildings.push({
                ...building,
                itemName: building.name,
            });
        });
    }
    
    setDataBuildings() { 
        if (this.ds.selectedItemData && this.ds.selectedItemData.corporationNumbers.length && this.buildings.length === 1) {
            this.ds.selectedItemData.buildings = JSON.parse(JSON.stringify(this.buildings));
        }
        this.loadPermissions(true);
    }

    getCorporationNumberList() {
        this.corporationNumbers = [];
        this.corporationNumbers = this.ds.corporations.map(item => ({
                                        ...item,
                                        itemName: item.name,
                                        disabled: item.disabled
                                    }));
        const parentCorporations = this.ds.getParentCorporations(this.corporationNumbers);
        this.data.corporationNumbers = parentCorporations && parentCorporations.length > 0 ? parentCorporations : [];
        if((!parentCorporations || parentCorporations.length < 1) && this.corporationNumbers.length === 1) {
            this.data.corporationNumbers = JSON.parse(JSON.stringify(this.corporationNumbers));
            this.getBuildingsForSelectedCorporations();
        }
        if (parentCorporations.length) {
            this.haveaParent = true;
            this.corporationNumbers = this.corporationNumbers.filter(item => {
                let hasMatchedCorporation = false;
                parentCorporations.forEach(element => {
                    if (String(item.id) === String(element.id)) {
                        hasMatchedCorporation = true;
                    }
                });
                return hasMatchedCorporation;
            });
            this.getParentBuildings();
            this.setDataBuildings();
        } 
        if ((!parentCorporations || parentCorporations.length == 0) && this.corporationNumbers.length > 1) { 
            this.buildings = JSON.parse(JSON.stringify(this.ds.multiselectBuildings));
        }
        
        this.isCorporationLoading = false;
    }

    ngOnDestroy() {
      this.ngUnsubscribe.next();
      this.ngUnsubscribe.complete();
    }

    @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
        setTimeout(() => this.ds.hideEditPermissionModal(), 100);
    }
}

class ItemObj {
    corporationNumbers: Array<any> = [];
    buildings: Array<any> = [];
    name: string = null;
    permissions: Array<any> = [];
    userGroups: Array<any> = [];
    isPublic: boolean;
}


// WEBPACK FOOTER //
// /app/src/app/views/documents/edit-permissions-modal/edit-permissions-modal.component.ts